// covered-by: frontends/carrie/src/utils/queries/__tests__/knowledge-db.test.tsx
/**
 * LEGACY + sibling-feature hooks that survived the knowledge refresh:
 * - useGetKnowledgeDataRecord: historical comment citation chips (the old
 *   record route is retired, the chips rely on the 404 fallback).
 * - persona / Ask Puntt skill / connector / custom-workflow mutations used by
 *   the Knowledge Settings page (unrelated to the knowledge database itself —
 *   they only ever lived in this file for convenience).
 * New sources/rules/activity hooks live in utils/queries/knowledge.tsx.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  updateAIPersona,
  updateAskPunttSkill,
  updateAskPunttEnabled,
  updateAskPunttConnector,
  createCustomWorkflow,
  updateCustomWorkflow,
  deleteCustomWorkflow,
} from "../../services/brand-guidelines";
import { getKnowledgeDataRecord } from "../../services/knowledge-db";
import { isNil } from "../fp";

export function useGetKnowledgeDataRecord(knowledgeDataRecordId?: string) {
  const query = useQuery({
    queryKey: ["knowledge-data-record", knowledgeDataRecordId],
    queryFn: () => getKnowledgeDataRecord(knowledgeDataRecordId!),
    enabled: !isNil(knowledgeDataRecordId),
  });

  return query;
}

export function useUpdatePersonaMutation() {
  return useMutation({
    mutationKey: ["ai-personas", "update"],
    mutationFn: updateAIPersona,
  });
}

export function useUpdateAskPunttSkillMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-skills", "update"],
    mutationFn: updateAskPunttSkill,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateAskPunttEnabledMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-enabled", "update"],
    mutationFn: updateAskPunttEnabled,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateAskPunttConnectorMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["askpuntt-connectors", "update"],
    mutationFn: updateAskPunttConnector,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useCreateCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "create"],
    mutationFn: createCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useUpdateCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "update"],
    mutationFn: updateCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}

export function useDeleteCustomWorkflowMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["custom-workflows", "delete"],
    mutationFn: deleteCustomWorkflow,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["brand-guidelines"] });
    },
  });
}
